import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SectionVariable } from '../types';
import { useSettings } from '../Settings';
import { fetchPlansCached } from '../api/middleware';
import { openSalesOrWhatsApp } from '../utils/links';
import { DisplayPlan, defaultDisplayPlans, mapApiPlansToDisplay, DEFAULT_PLAN_BADGE } from '../utils/planDisplay';
import { useTranslation, interpolate } from '../i18n';

const WhatsAppIconCircled = () => (
  <Box sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: '8px', bgcolor: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51h-.57c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M21.18 2.812A11.812 11.812 0 0012.053 0C5.462 0 .101 5.36.098 11.954c0 2.105.55 4.16 1.595 5.975L0 24l6.216-1.63a11.83 11.83 0 005.834 1.523h.005c6.589 0 11.951-5.361 11.955-11.955a11.832 11.832 0 00-2.83-8.126" fill="#fff"/>
    </svg>
  </Box>
);

const WazeIconCircled = () => (
  <Box sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: '8px', bgcolor: '#33CCFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.03 2 10.94c0 2.63 1.22 4.97 3.14 6.56-.06 1.87-.78 2.75-1.65 3.53-.24.22-.07.62.24.62 2.23 0 4.04-1.08 5.01-1.87.74.14 1.5.22 2.26.22 5.52 0 10-4.03 10-8.94V11.06C21.97 6.06 17.5 2 12 2zm-3 10.5c-.83 0-1.5-.67-1.5-1.5S8.17 9.5 9 9.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm6 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    </svg>
  </Box>
);

interface PlansProps {
  variables?: SectionVariable;
  name?: string;
}

const TopRightArrowCircled = ({ highlighted }: { highlighted?: boolean }) => {
  const color = highlighted ? "#00D4FF" : "#FFF";
  return (
    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#0000004D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 14.5L15 9M15 9H11.5M15 9V12.5" />
      </svg>
    </Box>
  );
};

export function Plans({ variables: _variables }: PlansProps) {
  const { settings } = useSettings();
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  function handleBuy() {
    const message = interpolate(t.whatsapp.salesMessage, {
      brand: settings.companyName || t.whatsapp.salesFallbackBrand,
    });
    openSalesOrWhatsApp(settings.salesUrl, settings.socials?.whatsapp, message);
  }

  function localizeBadge(badge: string | undefined): string | undefined {
    if (!badge) return badge;
    return badge === DEFAULT_PLAN_BADGE ? t.plans.mostPopular : badge;
  }
  const [plans, setPlans] = useState<DisplayPlan[]>(defaultDisplayPlans);
  const [loading, setLoading] = useState(false);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 344; // width (320) + gap (24)
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (!settings.middlewareUrl) return;

    let cancelled = false;
    setLoading(true);

    fetchPlansCached(settings.middlewareUrl)
      .then((apiPlans) => {
        if (cancelled) return;
        if (apiPlans.length > 0) {
          // MVNEs Surf não somam o bônus +5GB automaticamente na exibição
          setPlans(mapApiPlansToDisplay(apiPlans, {
            skipPortabilityBonus: settings.isSurfMVNE === true,
          }));
        }
      })
      .catch((err) => {
        console.error('Erro ao buscar planos da API:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [settings.middlewareUrl, settings.isSurfMVNE]);

  return (
      <Box
        id="planos"
        className="plans"
        sx={{
          px: { xs: 2, md: 4 },
          bgcolor: 'var(--color-primary, #6B00D6)',
          mt: { xs: -8, md: -18 },
          pt: { xs: 16, md: 28 },
          position: 'relative',
        }}
      >
        <Box maxWidth={1280} mx="auto">
          <Box
            sx={{
              display: 'inline-flex',
              bgcolor: '#00000036',
              borderRadius: '0.5625rem',
              px: '12px',
              py: '6px',
              color: '#FFF',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '0.625rem',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: 'normal',
              mb: 2,
            }}
          >
            {t.plans.tag}
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={6}>
            <Typography
              variant="h4"
              sx={{
                color: '#FFF',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '2.1875rem',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '2.5rem',
                maxWidth: '600px',
              }}
            >
              {t.plans.titleLine1}
              <br />
              {t.plans.titleLine2}
            </Typography>

            <Box display={{ xs: 'none', md: 'flex' }} gap={2}>
              <IconButton 
                onClick={() => scroll('left')} 
                sx={{ 
                  bgcolor: '#00000033', 
                  color: '#FFF', 
                  '&:hover': { bgcolor: '#0000004D' } 
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton 
                onClick={() => scroll('right')} 
                sx={{ 
                  bgcolor: '#00000033', 
                  color: '#FFF', 
                  '&:hover': { bgcolor: '#0000004D' } 
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress sx={{ color: '#fff' }} />
            </Box>
          ) : (
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              gap: 3,
              overflowX: 'auto',
              pt: 2,
              pb: 2,
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {plans.map((plan, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  minWidth: { xs: 280, md: 320 },
                  flex: '0 0 auto',
                  scrollSnapAlign: 'start',
                  borderRadius: '24px 24px 0 0',
                  bgcolor: '#00000026',
                  border: plan.highlighted
                    ? '2px solid #00D4FF'
                    : '1px solid #FFFFFF0D',
                  borderBottom: 'none',
                  overflow: 'visible',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 3,
                  color: '#fff',
                }}
              >
                {plan.badge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: '#00D4FF',
                      color: '#fff',
                      px: 3,
                      py: 0.5,
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      fontFamily: "'Figtree', sans-serif",
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {localizeBadge(plan.badge)}
                  </Box>
                )}

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Box
                    sx={{
                      border: '1px solid #FFFFFF40',
                      borderRadius: '20px',
                      padding: '4px 16px',
                      fontSize: '0.8rem',
                      fontFamily: "'Figtree', sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {plan.name}
                  </Box>
                  <Box component="button" onClick={handleBuy} sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0, display: 'flex' }}>
                    <TopRightArrowCircled highlighted={plan.highlighted} />
                  </Box>
                </Box>

                <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '3.5rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.05em' }}>
                  {plan.dataAmount}
                  <Typography component="span" sx={{ fontSize: '1.8rem', opacity: 0.7, fontWeight: 700, ml: 0.5, letterSpacing: 'normal' }}>
                    {plan.dataUnit}
                  </Typography>
                </Typography>
                
                <Typography sx={{ mt: 1.5, fontFamily: "'Figtree', sans-serif", fontSize: '0.85rem', opacity: 0.8, minHeight: '3.5em', lineHeight: 1.4 }}>
                  {plan.description}
                </Typography>

                {(() => {
                  const filteredApps = plan.appsIncluded.filter((app) => {
                    const isWA = app.toLowerCase().includes('whatsapp');
                    const isWZ = app.toLowerCase().includes('waze');
                    return !(settings.isSurfMVNE && (isWA || isWZ));
                  });

                  if (filteredApps.length === 0) return null;

                  return (
                    <Box mt={3} mb={1}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 1.5, fontFamily: "'Figtree', sans-serif" }}>
                        {t.plans.appsIncluded}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2.5}>
                        {filteredApps.map((app, appIdx) => {
                          const appName = app.trim();
                          const isWhatsApp = appName.toLowerCase().includes('whatsapp');
                          const isWaze = appName.toLowerCase().includes('waze');
                          return (
                            <Box key={appIdx} display="flex" alignItems="center" gap={1.5}>
                              {isWhatsApp && <WhatsAppIconCircled />}
                              {isWaze && <WazeIconCircled />}
                              {!isWhatsApp && !isWaze && <WhatsAppIconCircled />}
                              <Box>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: "'Figtree', sans-serif", lineHeight: 1.2 }}>
                                  {isWhatsApp ? 'Whatsapp' : isWaze ? 'Waze' : appName}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', opacity: 0.8, fontFamily: "'Figtree', sans-serif", lineHeight: 1.2 }}>{t.common.unlimited}</Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}

                <Box mt="auto" pt={4} display="flex" justifyContent="space-between" alignItems="flex-end">
                  <Box>
                    <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, mb: 0.5 }}>
                      {plan.price}
                    </Typography>
                    <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '0.8rem', opacity: 0.6 }}>
                      {t.plans.perMonth}
                    </Typography>
                  </Box>
                  <Box
                    component="button"
                    onClick={handleBuy}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: '#FFFFFF1A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        bgcolor: '#FFFFFF33',
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 12h10M13 8l4 4-4 4"/>
                    </svg>
                  </Box>
                </Box>

              </Paper>
            ))}
          </Box>
          )}
        </Box>
      </Box>
  );
}
